/**
 * Curated dual-host narration scripts — Forecasting module pages.
 * Keyed by ListenButton guideId. See index.js for the writing style guide.
 */

export const FORECASTING_NARRATIONS = {
  'maestro-reports-help': [
    {
      title: 'What this page is for',
      icon: '📥',
      turns: [
        {
          speaker: 'clara',
          speech: 'This is where a Maestro export lands. The grid you see is the spreadsheet itself, column for column. The portal does not recalculate those cells.',
        },
        {
          speaker: 'liam',
          speech: 'Each upload is its own snapshot. Before you confirm, you can type a cut-off date and a short note. Both are optional. A blank date or a blank note never stops the upload. They only help you tell one snapshot from another later.',
        },
        {
          speaker: 'clara',
          speech: 'The trash icon beside the snapshot removes that import. The audit log records who deleted it, the cut-off, the note, and how many rows it held. Forecast numbers already copied onto the Forecast page stay there.',
        },
      ],
    },
    {
      title: 'How the summary report is built',
      icon: '📄',
      turns: [
        {
          speaker: 'clara',
          speech: 'The nine zero nine one button does not print the grid. It rebuilds the summary report from the snapshot you have selected, one P D F per company.',
        },
        {
          speaker: 'liam',
          speech: 'Rows are grouped by company, then discipline, then phase, then activity. Several cost-type lines for the same activity are added together into one report line.',
        },
        {
          speaker: 'clara',
          speech: 'Hours estimated is the original budget quantity, summed for every cost type. Hours actual only counts labour rows. Revenue estimated is the revised selling budget. Revenue actual is billing. Cost estimated is the original budget, and cost actual is the actual cost. Remaining purchase orders are the P O balance. Total cost including committed is the total and P O column.',
        },
      ],
    },
    {
      title: 'What the portal calculates',
      icon: '🧮',
      turns: [
        {
          speaker: 'liam',
          speech: 'Three columns are not in the file. Hours variance is estimated hours minus actual hours. Revenue variance is estimated revenue minus actual revenue. Cost variance is estimated cost minus total cost including committed.',
        },
        {
          speaker: 'clara',
          speech: 'At the bottom of the project, estimated profit is estimated revenue minus estimated cost. Actual profit is actual revenue minus actual cost. Phase totals are just the sum of the lines above them.',
        },
        {
          speaker: 'liam',
          speech: 'A row counts as labour when its group is L Labour. Older files with no group fall back to a phase name that says labour or staff. Room and board, contract expenses, materials, rentals, and site expenses never count as hours.',
        },
      ],
    },
    {
      title: 'How the consolidated report is built',
      icon: '📊',
      turns: [
        {
          speaker: 'clara',
          speech: 'The consolidated button uses that same snapshot. By Discipline keeps each discipline in its own section. Roll-up uses the same columns, then merges a phase across every discipline and groups the phases into contract, extras, and revenue.',
        },
        {
          speaker: 'liam',
          speech: 'Each company gets two columns. Actual hours, labour rows only, and actual cost to date, which is actual plus accrued. Activity zero zero one zero five is left off this report. It still appears on the per-company nine zero nine one.',
        },
        {
          speaker: 'clara',
          speech: 'The total block adds the companies together. Estimated hours and actual hours are both labour only. On the nine zero nine one, estimated hours still include every quantity. Here they do not. Hours variance is estimated hours minus actual hours.',
        },
        {
          speaker: 'liam',
          speech: 'Contract revenue budget is the revised selling budget. Actual revenue to date is billing. Estimated cost is the original budget. Actual cost to date is actual plus accrued. Actual cost including purchase orders is the total and P O column.',
        },
      ],
    },
    {
      title: 'Consolidated formulas and currency',
      icon: '💱',
      turns: [
        {
          speaker: 'clara',
          speech: 'Three columns are calculated after that sum. Percent spent is cost including purchase orders divided by estimated cost, and it stays blank when estimated cost is zero. Profit or loss is revenue to date minus cost including purchase orders. Estimated additional billing is the contract revenue budget minus revenue to date.',
        },
        {
          speaker: 'liam',
          speech: 'A phase total adds the lines, then runs those three formulas again on the summed dollars. It does not add up the percents printed on the lines above.',
        },
        {
          speaker: 'clara',
          speech: 'Only the three cost columns convert, when a company currency differs from the master project. Revenue stays in the currency Maestro stored. Hours never convert. The grid opens in the project company currency. Native is still there when you want each company in its own dollars. That toggle is for reading only. Happy importing.',
        },
        {
          speaker: 'liam',
          speech: 'And how does the portal know which company the job belongs to?',
        },
        {
          speaker: 'clara',
          speech: 'It reads it off the estimate number. L G means Canada, F L means L G L Inc in the States, U S is U S A North, L I is Tanks, Q C is Quebec. If there is no estimate it falls back to the job number prefix, like sixty one for L G L Inc or seventy one for Canada. A manual Division on the project always wins. Hover the View dollars toggle and it tells you which one it used.',
        },
      ],
    },
  ],
  'forecasting-dashboard-help': [
    {
      title: 'The cost forecast at a glance',
      icon: '📊',
      turns: [
        {
          speaker: 'clara',
          speech: 'This dashboard is where project managers turn Maestro costs into a live forecast. Each row is one operation code, with budget, committed cost, and the forecast fields you edit.',
        },
        {
          speaker: 'liam',
          speech: 'Green cells are yours to type. Cream cells calculate themselves. Light blue is last period, so you can see what moved since you finalized.',
        },
      ],
    },
    {
      title: 'Percent complete, E T C, and F A C',
      icon: '🧮',
      turns: [
        {
          speaker: 'liam',
          speech: 'Two inputs drive the forecast: percent complete, and E T C — estimate to complete. F A C is forecast at completion, the estimated cost when the work is done.',
        },
        {
          speaker: 'clara',
          speech: 'If percent complete is above zero, F A C is committed cost divided by that percent. If percent is zero but you typed an E T C, F A C is committed cost plus that E T C.',
        },
        {
          speaker: 'liam',
          speech: 'And if you have entered neither percent complete nor E T C, F A C simply carries the original estimated cost — Orig Bud — until you start forecasting that line.',
        },
        {
          speaker: 'clara',
          speech: 'Since those two inputs drive each other, the grid shows the driver in bold. Whichever one you actually typed — percent complete or E T C — appears bold, and the other one was calculated from it.',
        },
        {
          speaker: 'liam',
          speech: 'And zero means reset. Type a zero into percent complete or into E T C and the row clears in one edit — both drivers go to zero and F A C falls back to the original budget. If the work is actually finished, say so with percent complete at one hundred instead.',
        },
      ],
    },
    {
      title: 'Quick edits',
      icon: '✏️',
      turns: [
        {
          speaker: 'clara',
          speech: 'You do not need a double click. Single-click any green cell — percent complete, E T C, notes, and the other editable fields — and type.',
        },
        {
          speaker: 'liam',
          speech: 'Change percent complete or E T C and the portal recalculates F A C, forecasted profit, and margin right away. Contract rows block revised selling overrides; extras allow them.',
        },
        {
          speaker: 'clara',
          speech: 'Need the same number on several rows? Drag the little blue square on percent complete or revised selling budget. It copies that value down or up the column, and skips contract rows for selling budget.',
        },
      ],
    },
    {
      title: 'Analytics you can share',
      icon: '📤',
      turns: [
        {
          speaker: 'liam',
          speech: 'Open Analytics for the project story — budget, margin, contract value, and E V M. Same live numbers as the grid, rolled up.',
        },
        {
          speaker: 'clara',
          speech: 'From the header you can download Excel, download a P D F, or email the P D F to anyone you type in. Same idea as piping progress analytics.',
        },
      ],
    },
    {
      title: 'Import and finalize',
      icon: '📥',
      turns: [
        {
          speaker: 'liam',
          speech: 'Import Data now asks which Maestro import you want — the newest is preselected, but any earlier snapshot is one dropdown away. The rows are rolled up into one line per operation code so selling budget and profit are not double counted.',
        },
        {
          speaker: 'clara',
          speech: 'On cross-border jobs the import also swaps currencies first: each company\u2019s dollars convert to the master project currency at the current exchange rate before the rollup, and the rate used is stamped on the import. A view dollars toggle in the toolbar lets you read the whole table in the other currency — display only, so editing locks while it is on.',
        },
        {
          speaker: 'clara',
          speech: 'When the period is done, Finalize copies current percent complete, E T C, F A C, and revised selling into the previous columns and downloads a P D F.',
        },
        {
          speaker: 'liam',
          speech: 'And those frozen previous columns survive a re-import now — along with your notes, matched by operation code. The current period entries start fresh, which is the point of a new period. So finalize first, then import without fear.',
        },
        {
          speaker: 'clara',
          speech: 'Everything you and the team do here is on the record: the audit button opens the change history — every edit with its before and after, every import with its batch, every finalize. The export button asks whether you want the filtered view or the complete table, in Excel either way.',
        },
        {
          speaker: 'liam',
          speech: 'One peek at what is coming: a choice of forecast at completion methods — a default for the whole project, plus a per activity override. The help modal lists it under next steps. That is the whole loop. Happy forecasting.',
        },
      ],
    },
  ],
};
